<template>
  <div class="keys-container mt-2">
    <h2 class="mb-4">API Access Tokens</h2>

    <b-alert v-for="(alert, index) in alerts"
             :key="`alert-${index}`"
             :variant="alert.variant"
             :show="7"
             dismissible fade>
      {{ alert.text }}
    </b-alert>

    <b-alert v-if="!doesUserHaveApiaccess"
             variant="danger"
             show>
      Error - This account does not have privileges to create API access tokens
    </b-alert>

    <b-row class="mb-4" v-if="doesUserHaveApiaccess">
      <b-col cols='12'>
        <button class="btn btn-primary" @click="showNewKeyModal">Create a new key</button>
      </b-col>
    </b-row>

    <b-modal-custom
      :modal-class.sync="$options.data.themeClass"
      v-model="modalShow"
      @ok="handleAddKeyModalOk"
      @hidden="resetNewKeyData"
      @cancel="resetNewKeyData" >

      <!-- modal header/footer >>> -->
      <template v-slot:modal-header="{ close }">
        <h3 v-if="isNewKeyAvailable">New Key Details</h3>
        <h3 v-else>Create a new key</h3>

        <b-button-close @click="close()">×</b-button-close>
      </template>


      <template v-slot:modal-footer="{ ok, cancel }">
        <template v-if="isNewKeyAvailable">
          <b-button @click="cancel()">Close</b-button>
        </template>
        <template v-else>
          <b-button @click="cancel()">Cancel</b-button>
          <b-button variant="primary" @click="ok()">Create</b-button>
        </template>
      </template>
      <!-- modal header/footer <<< -->

      <!-- new key data section -->
      <div v-if="isNewKeyAvailable" class="mb-3">
        <b-row>
          <b-col cols='12'>
            <b-form-group>
              <label><strong>API Key Id</strong></label>
              <b-form-input type="text" placeholder="API Key Id" v-model="newKey.apiKeyId" readonly/>
            </b-form-group>

            <b-form-group class="access-token">
              <label><strong>Access Token</strong></label>
              <b-form-input class="access-token__input" type="text" placeholder="Access Token" v-model="newKey.accessToken" @click="selectAllText" readonly />
              <b-form-text class="access-token__help-text">Keep the access token in a secret place, you won't be able to restore it later!</b-form-text>
            </b-form-group>

            <b-form-group>
              <label><strong>Expires</strong></label>
              <b-form-input type="text" placeholder="Expires" v-model="newKey.expires" readonly/>
            </b-form-group>

            <b-form-group>
              <label><strong>User Id</strong></label>
              <b-form-input type="text" placeholder="User Id" v-model="newKey.userId" readonly />
            </b-form-group>

            <b-form-group>
              <label><strong>Key Tag</strong></label>
              <b-form-input type="text" placeholder="Key Tag" v-model="newKey.keyTag" readonly />
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <!-- create a new key section -->
      <div v-else class="mb-3">
        <b-col cols='12'>
          <b-form-group>
            <label><strong>Tag</strong></label>
            <b-form-input v-model.trim="$v.newKey.tag.$model"
                          :state="checkState('tag')"
                          type="text" placeholder="Tag" required />

            <b-form-invalid-feedback>
              This is a required field and must be at least 3 characters.
            </b-form-invalid-feedback>
          </b-form-group>

          <b-form-group>
            <label><strong>Expiry Date</strong></label>
            <b-form-input v-model.trim="$v.newKey.exp.$model"
                          :state="checkState('exp')"
                          type="datetime-local" placeholder="Expiry date" required />

            <b-form-invalid-feedback>
              This is a required field and must be a valid datetime in the future (at least 1 hour from now).
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </div>
    </b-modal-custom>

    <div v-if="doesUserHaveApiaccess">
      <h3>Current Keys</h3>

      <div id="keys-table-container">
        <b-table :dark="darkMode"
                 :hover="true"
                 :striped="true"
                 :bordered="false"
                 :small="true"
                 :items="currentKeys"
                 :fields="fields">
          <template #cell(delete)="cell">
            <i class="fa fa-lg fa-remove remove-key" @click="deleteKey(cell)" />
          </template>
        </b-table>
      </div>
    </div>
    <div>
      For documentation on how to use the API, please visit our <a href="https://openapi.oceanbolt.com">API Documentation</a>.
    </div>
  </div>
</template>

<script>
  import moment from 'moment';

  import { mapActions, mapGetters } from 'vuex'
  import { validationMixin } from 'vuelidate'
  import { required, minLength } from 'vuelidate/lib/validators'
  import { futureDateValidator } from '@/shared/validators'
  import BModalCustom from '@/base_components/BModalCustom'

  const future1HourValidator = futureDateValidator(3600);

  export default {
    name: 'long-lived-api-keys',
    props: {
      authData: {
        type: Object,
        default: () => {}
      },
    },
    mixins: [ validationMixin ],
    components: {
      BModalCustom
    },
    validations: {
      newKey: {
        // these values will go to "this.newKey" implicitly!
        tag: {
          required,
          minLength: minLength(3),
        },
        exp: {
          required,
          future1HourValidator,
        },
      },
    },
    data() {
      return {
        modalShow: false,
        currentPage: 1,
        alerts: [],

        fields: [
          {
            key: 'keyTag',
            label: 'Api Identifier',
          },
          {
            key: 'expires',
            label: 'Expiry',
          },
          {
            key: 'apikeyId',
            label: 'Apikey ID',
          },
          {
            key: 'delete',
            label: 'Delete',
            class: 'text-center',
          },
        ],
        currentKeys: [],
        newKey: {
          // input parameters
          // a user specified key identifier
          tag: null,
          // key expiry date (this value must be in seconds since epoch e.g. Unix Time)
          exp: null,

          // output parameters (when a key is created API will return them)
          apiKeyId: null,
          accessToken: null,  // a secret key, isn't stored by API
          expires: null,
          userId: null,
          keyTag: null,
        },
      };
    },
    async created() {
      await this.loadKeys();

      // some help to prevent long date selecting
      this.newKey.exp = this.oneDayFromNowHtml5Date;
    },
    methods: {
      ...mapActions('api/apikeys', [
        'getLongLivedKeys',
        'createLongLivedKey',
        'deleteLongLivedKey',
      ]),
      showNewKeyModal() {
        this.modalShow = true;
      },
      resetNewKeyData() {
        Object.keys(this.newKey).forEach(
          (key) => { this.newKey[key] = null; }
        );
        this.newKey.exp = this.oneDayFromNowHtml5Date;  // some help to prevent long date selecting
        // don't show error on empty values on another modal show
        this.$v.newKey.$reset();
      },
      async loadKeys() {
        this.currentKeys = await this.getLongLivedKeys() || [];

        this.currentKeys.forEach((item) => {
          // dates are stored in unix format, but displayed in local timezone
          item.expires = moment.unix(item.expires).format('YYYY-MM-DD HH:mm:ss');
          item.delete = 'x';
        });
      },
      async handleAddKeyModalOk(ev) {
        ev.preventDefault();

        this.$v.newKey.$touch();
        if (this.$v.newKey.$anyError) {
          return;
        }

        let data = {
          keyTag: this.newKey.tag,
          // dates are displayed in local timezone but sends as an utc unix timestamp
          expires: moment(this.newKey.exp, 'YYYY-MM-DDTHH:mm:ss').utc().unix()
        };

        let result = await this.createLongLivedKey(data);
        if(result) {
          this.newKey = {
            tag: '',
            exp: -1,
            apiKeyId: result.apikeyId,
            accessToken: result.apikeySecret,
            expires: moment.unix(result.expires).format('YYYY-MM-DD HH:mm:ss'),
            userId: result.userId,
            keyTag: result.keyTag,
          };
          await this.loadKeys();  // refresh keys list
        } else {
          this.alerts.push({text: 'Unknown error, please contact us', variant: 'warning'});
          this.resetNewKeyData();
        }
      },
      deleteKey(item) {
        this.$bvModal.msgBoxConfirm(`Are you sure to remove the "${item.item.keyTag}" ?`, {
          title: 'Key removing confirmation',
          okVariant: 'danger',
          okTitle: 'YES',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: false,
        })
        .then(modalResult => {
          if (modalResult) {
            this.deleteLongLivedKey(item.item).then(async (result) => {
              this.alerts.push({text: `The key "${item.item.keyTag}" was removed successfully`, variant: 'info'});
              await this.loadKeys();
            });
          }
        })
        .catch(async error => {
          console.error('modal error', error);
          await this.loadKeys();
        })
      },
      checkState(fieldName) {
        const field = this.$v.newKey[fieldName];
        return !field.$dirty || !field.$invalid;
      },
      selectAllText(ev) {
        if(ev.target && ev.target.select) {
          ev.target.select();
        }
      },
    },
    computed: {
      ...mapGetters('gui', [
        'darkMode',
      ]),

      isNewKeyAvailable: function () {
        return [
          this.newKey.apiKeyId,
          this.newKey.accessToken,
          this.newKey.expires,
          this.newKey.userId,
          this.newKey.keyTag,
        ].every(Boolean);
      },
      oneDayFromNowHtml5Date() {
        return moment().add(1, 'years').format('YYYY-MM-DDTHH:mm:ss');
      },
      doesUserHaveApiaccess: function() {
        if (this.authData && this.authData.user_authorization && this.authData.user_authorization.permissions) {
          return this.authData.user_authorization.permissions.includes("apiaccess")
        }
      },
    },
  }
</script>

<style scoped lang="scss">
  .access-token {
    &__input {
      background-color: #1a6f95;
    }
    &__help-text {
      color: yellow !important;
      font-size: 90%;
    }
  }

  .keys-container {
    max-width: 800px;
  }

  .remove-key {
    cursor: pointer;
    color: #a80000;
    &:hover {
      color: red;
    }
  }
</style>

<style lang="scss">
  #keys-table-container {
    .table-sm th, .table-sm td {
      padding: 0.6rem;
    }
  }
</style>
